import React, { forwardRef, memo } from 'react'

import parse from 'html-react-parser'
import { Box, Text } from 'stardust'
import styled from 'styled-components'

import { CHAT_BUBBLE_WIDTH, RevealElementCSS, SPACERS } from '~/theme'
import colors from '~/ui-components/tokens/colors'
import { formatTimestamp, createTypography, toLocalDateTimeString } from '~/utils'

import ActionPills from './ActionPills'

const ChatBox = styled(Box)<{ alignSelf: string }>`
  width: ${CHAT_BUBBLE_WIDTH};
  max-width: 80%;
  align-self: ${(props) => props.alignSelf};
`

const ChatBubble = styled(Text)<{ borderColor: string; textReveal?: boolean }>`
  border-radius: ${SPACERS.md};
  border: ${(props) => `2px solid ${props.borderColor}`};
  background-color: ${colors.cosmicShade0};
  padding: 10px;
  margin: 5px 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  font-size: ${SPACERS.md};
  line-height: ${SPACERS.xl};
  ${(props) => props.textReveal && RevealElementCSS}
`

const Chat = forwardRef<
  HTMLDivElement,
  {
    message: Playground.AssistantConversationMessage
    isLog?: boolean
    isFetching?: boolean
    handleAssistActionPill?: (action: Playground.AssistActionPill) => void
  }
>((props, ref) => {
  const { message, isLog, isFetching = false, handleAssistActionPill } = props

  const isUser = message.sender === 'USER'
  const isError = Boolean(message.isError)
  const borderColor = isError ? colors.superGiantRed12 : isUser ? colors.solYellow7 : colors.pulsarPurple18
  const alignSelf = isUser ? 'flex-end' : 'flex-start'
  const textAlign = isUser ? 'right' : 'left'
  const feedbackMessage = message.feedbackMessage

  return (
    <ChatBox ref={ref} data-test={`chat_bubble_${message.sender}`} alignSelf={alignSelf}>
      <ChatBubble textReveal={message.withTextReveal} borderColor={borderColor} className="mceContent">
        <strong>{message.content.boldText}</strong>
        {parse(message.content.regularText)}
      </ChatBubble>

      {isLog && message.timestamp ? (
        <Text
          {...createTypography('caption')}
          color={colors.cosmicShade20}
          textAlign={textAlign}
          mb={SPACERS.xxs}
          mx={2}>
          {formatTimestamp(new Date(toLocalDateTimeString(message.timestamp)), 'hh:mma. D MMM, YYYY')}
        </Text>
      ) : null}

      {!isError && message.actionPills ? (
        <>
          {feedbackMessage ? (
            <ChatBubble textReveal={message.withTextReveal} borderColor={borderColor}>
              <strong>{feedbackMessage}</strong>
            </ChatBubble>
          ) : null}

          <ActionPills
            actionPills={message.actionPills}
            disabled={isFetching}
            handleAssistActionPill={handleAssistActionPill}
          />
        </>
      ) : null}
    </ChatBox>
  )
})
Chat.displayName = 'Chat'
export default memo(Chat)
